import type { ChapterContract } from './types'

/**
 * MBA 六章论证契约（行业常见结构，非学校模板硬套）。
 * 核心思想：每章只承担论证链上一环；禁止提前写后章、禁止脱离 RQ/框架。
 */
export const MBA_CHAPTER_CONTRACTS: readonly ChapterContract[] = [
  {
    chapterIndex: 1,
    title: '绪论',
    roleInArgument:
      '把「管理现场的痛」收成可研究的问题，并交代全文如何回答它。读者读完应知道：为何重要、问什么、用什么路径答。',
    mustAnswer: [
      '实践背景与痛点为何值得做一篇 MBA 论文？',
      '核心研究问题（及子问题）精确表述是什么？',
      '研究目的、意义（理论/实践）分别是什么？',
      '全文结构如何对应论证路径？',
    ],
    mustInclude: [
      '研究背景（行业/企业/岗位情境）',
      '问题提出（从现象到问题，避免空泛）',
      '研究目的与意义',
      '研究问题列表（与开题卡一致）',
      '研究内容与技术路线/全文结构',
    ],
    mustLinkToBrief: [
      'practiceBackground',
      'problemStatement',
      'researchPurpose',
      'researchQuestions',
      'scopeIn',
      'scopeOut',
    ],
    forbid: [
      '展开完整文献综述或理论推导（留给第二章）',
      '详细描述问卷题项、访谈提纲或数据分析结果（留给第三/四章）',
      '给出管理建议清单（留给第五章）',
      '改变或新增与开题卡冲突的研究问题而不提示修订开题卡',
    ],
    dependsOn: [],
    exitCriteria: [
      '研究问题表述可操作，与开题卡一致',
      '问题边界清晰（scopeOut 有呼应）',
      '结构图/说明能映射到后续五章',
    ],
    suggestedSections: ['研究背景', '问题提出', '研究目的与意义', '研究问题', '研究内容与结构'],
  },
  {
    chapterIndex: 2,
    title: '文献综述与理论基础',
    roleInArgument:
      '为 RQ 选定理论镜头与构念口径，标出研究空白；产出「可用来分析证据」的框架，而不是文献堆砌。',
    mustAnswer: [
      '与本 RQ 直接相关的理论与主要研究脉络是什么？',
      '关键构念如何界定？彼此关系如何？',
      '既有研究缺口在哪里，本论文如何切入？',
      '分析框架如何把构念组织成可检验/可解释的逻辑？',
    ],
    mustInclude: [
      '与 RQ 对齐的文献综述（按主题/构念组织，忌年代流水账）',
      '理论基础与理论视角选择理由',
      '关键构念定义表（与开题卡一致）',
      '研究空白与本文定位',
      '分析框架图或等价文字说明',
    ],
    mustLinkToBrief: [
      'researchQuestions',
      'theoreticalLens',
      'keyConstructs',
      'analysisFramework',
      'hypothesesOrPropositions',
    ],
    forbid: [
      '与 RQ 无关的宽泛综述「炫博览」',
      '未定义就使用的核心术语',
      '用第四章式的案例细节替代理论论证',
      '偷偷更换理论镜头而不回写开题卡',
    ],
    dependsOn: [1],
    exitCriteria: [
      '理论镜头与构念口径固定，可供第三、四章沿用',
      '分析框架能覆盖全部 RQ',
      '明确本文相对文献的增量（空白→切入）',
    ],
    suggestedSections: [
      '相关研究现状',
      '理论基础',
      '关键概念界定',
      '研究空白',
      '分析框架',
    ],
  },
  {
    chapterIndex: 3,
    title: '研究方法与设计',
    roleInArgument:
      '把框架「落地」为可执行的取证与分析设计；读者应能判断：证据从哪来、如何编码/测量、如何保证可信。',
    mustAnswer: [
      '为何选该方法（案例/问卷/访谈/混合）？与 RQ 匹配吗？',
      '分析单位、案例或样本如何界定与选择？',
      '数据来源、收集步骤、时间窗口是什么？',
      '如何分析（编码、统计、三角互证）？信效度/可信性如何保障？',
    ],
    mustInclude: [
      '方法选择与适切性论证',
      '分析单位与案例/样本说明',
      '数据来源与收集过程',
      '分析步骤（对应分析框架）',
      '信效度 / 可信性、伦理与局限预案',
    ],
    mustLinkToBrief: [
      'researchQuestions',
      'methodType',
      'unitOfAnalysis',
      'caseOrSample',
      'dataSources',
      'analysisPlan',
      'keyConstructs',
      'analysisFramework',
    ],
    forbid: [
      '提前报告发现与结论（留给第四、五章）',
      '方法与 RQ/框架脱节（例如框架是构念关系却只做无关描述统计）',
      '虚构具体数据或访谈原话冒充已采集',
    ],
    dependsOn: [1, 2],
    exitCriteria: [
      '设计足以回答全部 RQ',
      '构念操作化可追溯到第二章定义',
      '分析步骤与分析框架一一对应',
    ],
    suggestedSections: [
      '方法选择',
      '案例/样本',
      '数据收集',
      '分析策略',
      '信效度与伦理',
    ],
  },
  {
    chapterIndex: 4,
    title: '案例/数据分析',
    roleInArgument:
      '按 RQ（或假设）呈现证据与发现；「证据→发现」链条清楚，禁止变成公司介绍或流水账。',
    mustAnswer: [
      '针对每个 RQ，证据是什么、发现是什么？',
      '发现如何映射到分析框架中的构念关系？',
      '矛盾证据或边界条件如何处理？',
    ],
    mustInclude: [
      '案例/样本与数据概况（简要，细节服从 RQ）',
      '按 RQ 或构念分节的证据呈现',
      '发现小结（可对应假设是否支持）',
      '必要时：三角互证或稳健性说明',
    ],
    mustLinkToBrief: [
      'researchQuestions',
      'keyConstructs',
      'analysisFramework',
      'hypothesesOrPropositions',
      'caseOrSample',
      'dataSources',
    ],
    forbid: [
      '大段与 RQ 无关的企业宣传/历史沿革',
      '跳过证据直接给管理建议（留给第五章）',
      '引入第二章未界定的新构念却不回修开题卡/第二章',
      '编造数据、访谈原话或统计结果',
    ],
    dependsOn: [1, 2, 3],
    exitCriteria: [
      '每个 RQ 都有对应证据与发现',
      '发现表述可被第五章回扣理论',
      '未越权写实践处方',
    ],
    suggestedSections: ['数据与情境概述', '研究发现（按 RQ）', '小结'],
  },
  {
    chapterIndex: 5,
    title: '讨论与管理启示',
    roleInArgument:
      '把第四章发现回扣理论与实践：解释「为什么」、与文献对话、给出可执行但边界清晰的管理启示。',
    mustAnswer: [
      '发现如何支持/修正/挑战既有理论或框架？',
      '对管理者意味着什么？建议的适用条件与风险？',
      '与第二章文献的对话点在哪里？',
    ],
    mustInclude: [
      '发现的理论解释（回扣框架/构念）',
      '与既有研究的对话',
      '管理启示（可执行、可检验、带边界）',
      '启示与 RQ/问题陈述的对应关系',
    ],
    mustLinkToBrief: [
      'researchQuestions',
      'problemStatement',
      'theoreticalLens',
      'analysisFramework',
      'expectedContributions',
      'scopeOut',
    ],
    forbid: [
      '重复粘贴第四章证据表而无解释',
      '空泛口号式建议（「加强重视」「提升意识」）',
      '提出超出研究证据与 scopeOut 的政策处方',
      '引入全新 RQ 却当作已研究结论',
    ],
    dependsOn: [1, 2, 3, 4],
    exitCriteria: [
      '每个主要发现都有理论回扣',
      '管理启示可追溯到证据与 RQ',
      '建议带适用边界',
    ],
    suggestedSections: ['结果讨论', '理论贡献对话', '管理启示', '实施边界'],
  },
  {
    chapterIndex: 6,
    title: '结论与展望',
    roleInArgument:
      '收束全文：贡献、局限、未来研究；不再展开新分析或新建议清单。',
    mustAnswer: [
      '研究结论如何一句话回扣核心 RQ？',
      '理论与实践贡献各是什么？',
      '主要局限与可跟进的研究问题？',
    ],
    mustInclude: [
      '研究结论（对应 RQ）',
      '研究贡献',
      '局限',
      '未来研究展望',
    ],
    mustLinkToBrief: [
      'researchQuestions',
      'expectedContributions',
      'scopeOut',
      'problemStatement',
    ],
    forbid: [
      '新开未分析过的数据或案例情节',
      '大段重复第五章程式建议',
      '夸大贡献或隐瞒关键局限',
    ],
    dependsOn: [1, 2, 3, 4, 5],
    exitCriteria: [
      '结论与开题卡 RQ 一一对应',
      '局限诚实且与方法设计一致',
      '无新证据、无新 RQ',
    ],
    suggestedSections: ['研究结论', '研究贡献', '局限与展望'],
  },
] as const

export function getChapterContract(chapterIndex: number): ChapterContract | null {
  return MBA_CHAPTER_CONTRACTS.find((c) => c.chapterIndex === chapterIndex) ?? null
}

/** 从章节标题推断 1–6；失败返回 null */
export function inferChapterIndexFromTitle(title: string): number | null {
  const t = String(title || '')
  const m = t.match(/第\s*([一二三四五六1-6])\s*章/)
  if (!m) return null
  const map: Record<string, number> = {
    一: 1,
    二: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
    '5': 5,
    '6': 6,
  }
  return map[m[1]] ?? null
}
